import React, { useMemo } from "react";
import { useFetchExperiments } from "../Hooks/AdoHooks/UseFetchExperiments";
import { mergeStyleSets, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import { styles as sharedStyles } from "../Shared/shared.styles";
import { ExperimentType } from "../Types";
import ExperimentOverview from "./ExperimentOverview/ExperimentOverview";
import ExperimentDetails from "./ExperimentDetails/ExperimentDetails";
import RolloutDetails from "./RolloutDetails/RolloutDetails";
import { useValidateExperimentParam } from "../Hooks/UseValidatePathParams";
import ExperimentBreadcrumbs from "../Shared/ExperimentBreadcrumbs";
import { useEv2ExperimentParams } from "../Hooks/UseCurrentExtension";
import { useAppContext } from "../Contexts";
import { ARCHIVE_KEY, DEFAULT_EXPERIMENT } from "../Shared/shared.consts";

interface ExperimentProps {
  /**
   * When true show experiment creation form
   */
  isCreate: boolean;
  /**
   * When true show experiment cloning form with pre-filled data
   */
  isClone: boolean;
}

/**
 * Main experiment component that handles loading and routing between
 * create, clone, and update experiment views based on props (isCreate, isClone).
 */
const Experiment: React.FC<ExperimentProps> = ({ isCreate, isClone }) => {
  const { setError } = useAppContext();
  const { experimentName = "" } = useEv2ExperimentParams();
  const {
    value: { experiments, archivedExperiments } = {
      experiments: undefined,
      archivedExperiments: undefined,
    },
    loading: loadingExperiments,
  } = useFetchExperiments(setError);

  const allExperiments = useMemo(() => {
    if (!experiments && !archivedExperiments) {
      return undefined;
    }
    return [...(experiments || []), ...(archivedExperiments || [])];
  }, [experiments, archivedExperiments]);

  // Validate the experiment in the URL params
  useValidateExperimentParam(allExperiments);

  if (loadingExperiments || !allExperiments) {
    return (
      <div className={sharedStyles.loadingSpinner}>
        <Spinner label="Loading experiment..." size={SpinnerSize.large} />
      </div>
    );
  }

  let content;

  if (isClone) {
    content = (
      <CloneExperiment
        experiments={allExperiments}
        experimentKey={experimentName}
      />
    );
  } else if (isCreate) {
    content = <CreateExperiment experiments={allExperiments} />;
  } else {
    content = (
      <UpdateExperiment
        experiments={allExperiments}
        experimentKey={experimentName}
      />
    );
  }

  return (
    <>
      <ExperimentBreadcrumbs isClone={!!isClone} isCreate={!!isCreate} />
      {content}
    </>
  );
};

interface UpdateExperimentProps {
  /**
   * List of all experiments for the current extension
   */
  experiments: ExperimentType[];
  /**
   * Unique identifier for experiment
   */
  experimentKey: string;
}

/**
 * Component for viewing and editing an existing experiment.
 * Shows overview, form, and rollout information.
 */
const UpdateExperiment: React.FC<UpdateExperimentProps> = ({
  experiments,
  experimentKey,
}) => {
  const selectedExperiment = useMemo(
    () => experiments.find((exp) => exp.featureKey === experimentKey),
    [experiments, experimentKey],
  );

  if (!selectedExperiment) {
    return null;
  }

  const isArchived = selectedExperiment.__filename.includes(ARCHIVE_KEY);

  return (
    <>
      <ExperimentOverview
        experimentDetails={selectedExperiment}
        isArchived={isArchived}
      />
      <Stack className={styles.experimentContainer}>
        <ExperimentDetails
          experiments={experiments}
          experimentDetails={selectedExperiment}
          isCreate={false}
          isReadOnly={isArchived}
        />
        <RolloutDetails
          experimentDetails={selectedExperiment}
          isReadOnly={isArchived}
        />
      </Stack>
    </>
  );
};

/**
 * Component for cloning an existing experiment.
 * Pre-fills form with data from the selected experiment. Will now show rollout details or overview.
 */
const CloneExperiment: React.FC<UpdateExperimentProps> = ({
  experiments,
  experimentKey,
}) => {
  const selectedExperiment = useMemo(
    () => experiments.find((exp) => exp.featureKey === experimentKey),
    [experiments, experimentKey],
  );
  return (
    <Stack className={styles.experimentContainer}>
      <ExperimentDetails
        experiments={experiments}
        experimentDetails={selectedExperiment}
        isClone={true}
      />
    </Stack>
  );
};

interface CreateExperimentProps {
  /**
   * List of all experiments for the current extension
   */
  experiments: ExperimentType[];
}

/**
 * Component for creating a new experiment.
 * Will pre-fill form extension values with data from the most recent successful experiment.
 */
const CreateExperiment: React.FC<CreateExperimentProps> = ({ experiments }) => {
  // Create prefilled experiment details (mimicking clone behavior)

  return (
    <Stack className={styles.experimentContainer}>
      <ExperimentDetails
        experiments={experiments}
        experimentDetails={DEFAULT_EXPERIMENT}
        isCreate={true}
      />
    </Stack>
  );
};

const styles = mergeStyleSets({
  experimentContainer: {
    height: "100%",
    minHeight: 0,
    overflow: "auto",
    padding: "10px 0",
  },
});

export default Experiment;
